import { pickField } from "@/utility/fclReportFieldUtils";
import {
    clipText,
    dateReportCol,
    fileRefLinkCell,
    pickDateSort,
    pickSort,
    reportCol,
} from "./importReportColumnUtils";

const formatLocation = (row, nameKeys, customizedKeys, codeKeys) => {
    const name = pickField(row, nameKeys);
    const customized = pickField(row, customizedKeys);
    const code = pickField(row, codeKeys);

    if (name && /\[[^\]]+\]/.test(name)) return String(name).trim();
    const display = customized || name;
    if (!display) return "";
    if (code && !String(display).includes("[")) return `${display} [${code}]`;
    return String(display).trim();
};

export const IMPORT_ARRIVAL_NOTICE_LIST_COLUMNS = [
    reportCol({
        key: "fileRefNo",
        label: "File Ref #",
        width: 145,
        sticky: "left",
        render: (row) =>
            fileRefLinkCell(
                pickField(row, ["fileRefNo", "FileRefNo", "fileRefNumber", "FileRefNumber"])
            ),
        getTooltip: (row) =>
            pickField(row, ["fileRefNo", "FileRefNo", "fileRefNumber", "FileRefNumber"]) || "",
        sortValue: (row) => pickSort(row, ["fileRefNo", "FileRefNo", "fileRefNumber", "FileRefNumber"]),
    }),
    reportCol({
        key: "hblNumber",
        label: "HBL Number",
        width: 130,
        render: (row) =>
            clipText(pickField(row, ["hblNumber", "HblNumber", "HBLNumber"]), "font-mono text-[12px]"),
        getTooltip: (row) => pickField(row, ["hblNumber", "HblNumber", "HBLNumber"]) || "",
        sortValue: (row) => pickSort(row, ["hblNumber", "HblNumber", "HBLNumber"]),
    }),
    reportCol({
        key: "carrierMasterBL",
        label: "Carrier Master B/L",
        width: 140,
        render: (row) =>
            clipText(
                pickField(row, ["carrierMasterBL", "CarrierMasterBL", "carrierMBL", "CarrierMBL"]),
                "font-mono text-[12px]"
            ),
        sortValue: (row) => pickSort(row, ["carrierMasterBL", "CarrierMasterBL", "carrierMBL", "CarrierMBL"]),
    }),
    reportCol({
        key: "amsHouseBL",
        label: "AMS House B/L",
        width: 130,
        render: (row) =>
            clipText(pickField(row, ["amsHouseBL", "AmsHouseBL", "AMSHouseBL"]), "font-mono text-[12px]"),
        sortValue: (row) => pickSort(row, ["amsHouseBL", "AmsHouseBL", "AMSHouseBL"]),
    }),
    dateReportCol({
        key: "blDate",
        label: "B/L Date",
        width: 165,
        fieldKeys: ["blDate", "BlDate", "BLDate"],
    }),
    reportCol({
        key: "consignee",
        label: "Consignee",
        width: 185,
        render: (row) => clipText(pickField(row, ["consignee", "Consignee"])),
        sortValue: (row) => pickSort(row, ["consignee", "Consignee"]),
    }),
    reportCol({
        key: "customerPO",
        label: "Customer PO #",
        width: 125,
        render: (row) =>
            clipText(
                pickField(row, ["customerPO", "CustomerPO", "customerPONumber", "CustomerPONumber"])
            ),
        sortValue: (row) =>
            pickSort(row, ["customerPO", "CustomerPO", "customerPONumber", "CustomerPONumber"]),
    }),
    reportCol({
        key: "vessel",
        label: "Vessel",
        width: 185,
        render: (row) =>
            clipText(pickField(row, ["vessel", "Vessel", "vesselName", "VesselName"])),
        getTooltip: (row) => pickField(row, ["vessel", "Vessel", "vesselName", "VesselName"]) || "",
        sortValue: (row) => pickSort(row, ["vessel", "Vessel", "vesselName", "VesselName"]),
    }),
    reportCol({
        key: "container",
        label: "Container #",
        width: 195,
        render: (row) =>
            clipText(
                pickField(row, ["container", "Container", "containerNo", "ContainerNo"]),
                "font-mono text-[12px]"
            ),
        getTooltip: (row) =>
            pickField(row, ["container", "Container", "containerNo", "ContainerNo"]) || "",
        sortValue: (row) => pickSort(row, ["container", "Container", "containerNo", "ContainerNo"]),
    }),
    reportCol({
        key: "salesPerson",
        label: "SalesPerson",
        width: 130,
        render: (row) => clipText(pickField(row, ["salesPerson", "SalesPerson"])),
        sortValue: (row) => pickSort(row, ["salesPerson", "SalesPerson"]),
    }),
    reportCol({
        key: "carrier",
        label: "Carrier",
        width: 130,
        render: (row) => clipText(pickField(row, ["carrier", "Carrier"])),
        sortValue: (row) => pickSort(row, ["carrier", "Carrier"]),
    }),
    reportCol({
        key: "origin",
        label: "Origin",
        width: 175,
        render: (row) =>
            clipText(
                formatLocation(
                    row,
                    ["origin", "Origin"],
                    ["origin_Customized", "Origin_Customized", "originCustomized", "OriginCustomized"],
                    ["originCode", "OriginCode"]
                )
            ),
        getTooltip: (row) =>
            formatLocation(
                row,
                ["origin", "Origin"],
                ["origin_Customized", "Origin_Customized", "originCustomized", "OriginCustomized"],
                ["originCode", "OriginCode"]
            ),
        sortValue: (row) =>
            formatLocation(
                row,
                ["origin", "Origin"],
                ["origin_Customized", "Origin_Customized", "originCustomized", "OriginCustomized"],
                ["originCode", "OriginCode"]
            ).toLowerCase(),
    }),
    reportCol({
        key: "pol",
        label: "POL",
        width: 165,
        render: (row) =>
            clipText(
                formatLocation(
                    row,
                    ["pol", "POL"],
                    ["polCustomized", "PolCustomized"],
                    ["polCode", "PolCode"]
                )
            ),
        getTooltip: (row) =>
            formatLocation(row, ["pol", "POL"], ["polCustomized", "PolCustomized"], ["polCode", "PolCode"]),
        sortValue: (row) =>
            formatLocation(row, ["pol", "POL"], ["polCustomized", "PolCustomized"], ["polCode", "PolCode"]).toLowerCase(),
    }),
    reportCol({
        key: "pod",
        label: "POD",
        width: 165,
        render: (row) =>
            clipText(
                formatLocation(
                    row,
                    ["pod", "POD"],
                    ["podCustomized", "PodCustomized"],
                    ["podCode", "PodCode"]
                )
            ),
        getTooltip: (row) =>
            formatLocation(row, ["pod", "POD"], ["podCustomized", "PodCustomized"], ["podCode", "PodCode"]),
        sortValue: (row) =>
            formatLocation(row, ["pod", "POD"], ["podCustomized", "PodCustomized"], ["podCode", "PodCode"]).toLowerCase(),
    }),
    reportCol({
        key: "dest",
        label: "Dest.",
        width: 165,
        render: (row) =>
            clipText(
                formatLocation(
                    row,
                    ["dest", "Dest", "destination", "Destination"],
                    ["destCustomized", "DestCustomized"],
                    ["destCode", "DestCode"]
                )
            ),
        getTooltip: (row) =>
            formatLocation(
                row,
                ["dest", "Dest", "destination", "Destination"],
                ["destCustomized", "DestCustomized"],
                ["destCode", "DestCode"]
            ),
        sortValue: (row) =>
            formatLocation(
                row,
                ["dest", "Dest", "destination", "Destination"],
                ["destCustomized", "DestCustomized"],
                ["destCode", "DestCode"]
            ).toLowerCase(),
    }),
    dateReportCol({
        key: "docCO",
        label: "Doc C/O",
        width: 130,
        fieldKeys: ["docCO", "DocCO", "docCutOff", "DocCutOff"],
    }),
    dateReportCol({
        key: "ets",
        label: "ETS",
        width: 130,
        fieldKeys: ["ets", "ETS", "etd", "ETD"],
    }),
    dateReportCol({
        key: "eta",
        label: "ETA",
        width: 130,
        fieldKeys: ["eta", "ETA", "etapod", "ETAPOD"],
    }),
];

/** @deprecated Use IMPORT_ARRIVAL_NOTICE_LIST_COLUMNS */
export const IMPORT_ARRIVAL_NOTICE_COLUMNS = IMPORT_ARRIVAL_NOTICE_LIST_COLUMNS;
